// ════════════════════════════════════════════════════════════════════════════
// TarefasFiltrosBar — r154
// Barra de filtros completa, usada na Lista e no Calendário de Tarefas.
//
// Modo controlled: recebe `filtros` + `setFiltros` do pai.
//
// Props:
//   filtros: estado dos filtros (formato compatível com TarefasPageLista)
//   setFiltros: setter (filtros) => void
//   tarefas: lista de tarefas pra extrair opções de Cliente/Projeto/etc
//   showActions: se mostra o botão "+ Nova Tarefa" no fim da barra (default true)
//   onNovaTarefa: callback do botão (se showActions)
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useMemo } from 'react';
import SelectOrdemTarefas, { IconeBusca } from './SelectOrdemTarefas';   // r220
import BtnIcone from './BtnIcone';
import SePode from './SePode';

const COR = {
  azul:     '#003399', azulBg:   '#eff6ff',
  borda:    '#e8eaed', texto:    '#1a1a2e', muted: '#888',
  branco:   '#fff',    cinzaBg:  '#fafafa',
  verde:    '#10b981', verdeBg:  '#f0fff4',
  // r178: verde exclusivo do status 'Em Andamento' (nao reaproveitar noutros
  //       elementos -- COR.verde segue valendo pro resto).
  verdeAndamento: '#009900',
  vermelho: '#dc2626', vermBg:   '#fff0f0',
  amarelo:  '#d97706', amlBg:    '#fffbeb',
  // r182: amarelo do status 'Pendente de Retorno' (o COR.amarelo do Pausado
  //       e mais ambar -- os dois precisam ser distinguiveis lado a lado).
  amareloRetorno: '#eab308', amlRetBg: '#fefce8',
  roxo:     '#7c3aed', roxoBg:   '#f3e8ff',
  laranja:  '#f97316', laranjaBg:'#fff7ed',
  cinza:    '#6b7280', cinzaSt:  '#f3f4f6',
  // r178: cinza exclusivo do status 'Nao Iniciado' (COR.cinza segue valendo
  //       pro resto -- este e claro demais pra texto sobre branco).
  cinzaNaoIniciado: '#CCCCCC',
  // r220: Pausado virou azul claro e Pendente Cham. Filho virou laranja.
  //       Chaves proprias — COR.azul e COR.laranja seguem valendo pro resto.
  azulClaroPausado: '#38BDF8', azulClaroPausadoBg: '#f0f9ff',
  laranjaFilho:     '#F97316', laranjaFilhoBg:     '#fff7ed',
};

const STATUS_EXECUCAO = {
  N: { label: 'Não Iniciado',          cor: COR.cinzaNaoIniciado,    bg: COR.cinzaSt   },
  E: { label: 'Em Andamento',          cor: COR.verdeAndamento,    bg: COR.verdeBg  },
  X: { label: 'Pausado',               cor: COR.azulClaroPausado, bg: COR.azulClaroPausadoBg },
  R: { label: 'Pendente de Retorno',   cor: COR.amareloRetorno, bg: COR.amlRetBg },
  P: { label: 'Pendente Cham. Filho',  cor: COR.laranjaFilho, bg: COR.laranjaFilhoBg },
  F: { label: 'Finalizado',            cor: COR.vermelho, bg: COR.vermBg  },
  S: { label: 'Sandbox',               cor: COR.laranja,  bg: COR.laranjaBg },
};
const SETOR = {
  C: { label: 'Comercial',     cor: '#2563eb' },
  T: { label: 'Técnico',        cor: '#7c3aed' },
  Q: { label: 'Qualidade',      cor: '#f97316' },
  A: { label: 'Administrativo', cor: '#6b7280' },
  P: { label: 'Apoio Técnico',  cor: '#db2777' },
  O: { label: 'Outros',         cor: '#06b6d4' },
};
const PRIORIDADE = {
  A: { label: 'Alta',  cor: COR.vermelho },
  M: { label: 'Média', cor: COR.amarelo  },
  B: { label: 'Baixa', cor: COR.verde    },
};

// ─── FILTROS_DEFAULT exportável ────────────────────────────────────────────
export const FILTROS_DEFAULT = {
  busca:            '',
  status_execucao:  ['N','E','X','P'],
  setor:            [],
  prioridade:       [],
  cliente_ids:      [],
  negocio_ids:      [],
  solicitante_ids:  [],
  responsavel_ids:  [],
  data_prog_de:     '',
  data_prog_ate:    '',
  deadline_de:      '',
  deadline_ate:     '',
  papel:            'atribuidas',
  ordem:            'recente',
};

// ─── normalizarFiltros — saneia o que veio do localStorage ─────────────────
// r171: os códigos de `papel` mudaram. Como a tela persiste os filtros no
// navegador, usuários voltam com o valor antigo gravado e nenhum botão ficaria
// aceso. Traduz o legado na leitura (o backend também aceita, por segurança).
const PAPEL_LEGADO = { minhas: 'atribuidas', criadas_outros: 'criadas_para_outros' };
export const normalizarFiltros = (f) => ({
  ...f,
  papel: PAPEL_LEGADO[f?.papel] || f?.papel || FILTROS_DEFAULT.papel,
});

// ─── MultiSelect (cópia da Lista) ──────────────────────────────────────────
function MultiSelect({ label, opcoes, valores, onChange, largura, compacto }) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleValor = (v) => {
    if (valores.includes(v)) onChange(valores.filter(x => x !== v));
    else onChange([...valores, v]);
  };

  const limpar = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  let textoBotao;
  if (valores.length === 0) textoBotao = label;
  else if (valores.length === 1) {
    const op = opcoes.find(o => o.v === valores[0]);
    textoBotao = op?.label || valores[0];
  }
  else if (valores.length === 2) {
    textoBotao = valores.map(v => opcoes.find(o => o.v === v)?.label || v).join(', ');
  }
  else {
    textoBotao = `${valores.length} selecionados`;
  }

  const temSelecao = valores.length > 0;
  const h = compacto ? '28px' : '32px';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: largura || '140px' }}>
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        style={{
          width: '100%', height: h,
          padding: '0 8px',
          paddingRight: temSelecao ? '24px' : '20px',
          border: `1px solid ${temSelecao ? COR.azul : COR.borda}`,
          borderRadius: '4px',
          backgroundColor: temSelecao ? COR.azulBg : COR.branco,
          color: temSelecao ? COR.azul : COR.texto,
          fontSize: '11px', fontWeight: temSelecao ? '600' : '500',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '4px', fontFamily: "'Manrope', sans-serif",
          textAlign: 'left', overflow: 'hidden',
          whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          position: 'relative',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
          {textoBotao}
        </span>
        <span style={{ fontSize: '8px', flexShrink: 0 }}>▾</span>
        {temSelecao && (
          <span
            role="button"
            onClick={limpar}
            style={{
              position: 'absolute', right: '4px', top: '50%',
              transform: 'translateY(-50%)',
              width: '14px', height: '14px', borderRadius: '50%',
              backgroundColor: COR.azul, color: COR.branco,
              fontSize: '9px', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
            title="Limpar seleção"
          >×</span>
        )}
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0,
          marginTop: '2px', minWidth: '100%',
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`,
          borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100, maxHeight: '280px', overflowY: 'auto',
          padding: '4px',
        }}>
          {opcoes.length === 0 ? (
            <div style={{
              padding: '8px', fontSize: '11px',
              color: COR.muted, fontStyle: 'italic',
              textAlign: 'center',
            }}>Nenhuma opção</div>
          ) : opcoes.map(o => (
            <label key={o.v} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '5px 8px', borderRadius: '3px',
              cursor: 'pointer', fontSize: '11px',
              backgroundColor: valores.includes(o.v) ? COR.azulBg : 'transparent',
            }}
            onMouseEnter={e => { if (!valores.includes(o.v)) e.currentTarget.style.backgroundColor = COR.cinzaBg; }}
            onMouseLeave={e => { if (!valores.includes(o.v)) e.currentTarget.style.backgroundColor = 'transparent'; }}>
              <input
                type="checkbox"
                checked={valores.includes(o.v)}
                onChange={() => toggleValor(o.v)}
                style={{ cursor: 'pointer' }}
              />
              {o.cor && (
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  backgroundColor: o.cor, flexShrink: 0,
                }} />
              )}
              <span style={{ color: COR.texto, flex: 1 }}>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Barra de filtros principal ────────────────────────────────────────────
function TarefasFiltrosBar({
  filtros, setFiltros,
  tarefas = [],
  showActions = true,
  onNovaTarefa,
  totalRegistros,
}) {
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);

  const atualizarFiltro = (campo, valor) => {
    setFiltros(f => ({ ...f, [campo]: valor }));
  };

  // Conta filtros "ativos" (diferentes do default)
  const totalFiltrosAtivos = useMemo(() => {
    let n = 0;
    const statusDefault = ['N','E','X','P'];
    if (filtros.status_execucao.length !== statusDefault.length
        || filtros.status_execucao.some(s => !statusDefault.includes(s))) n++;
    if (filtros.setor.length > 0) n++;
    if (filtros.prioridade.length > 0) n++;
    if (filtros.cliente_ids.length > 0) n++;
    if (filtros.negocio_ids.length > 0) n++;
    if (filtros.solicitante_ids.length > 0) n++;
    if (filtros.responsavel_ids.length > 0) n++;
    if (filtros.data_prog_de || filtros.data_prog_ate) n++;
    if (filtros.deadline_de || filtros.deadline_ate) n++;
    return n;
  }, [filtros]);

  // Opções dinâmicas extraídas da lista
  const opcoesClientes = useMemo(() => {
    const m = new Map();
    tarefas.forEach(t => {
      if (t.cliente_id && !m.has(t.cliente_id)) {
        m.set(t.cliente_id, t.cliente_fantasia || t.cliente_nome || t.cliente_id);
      }
    });
    return Array.from(m.entries()).map(([v, label]) => ({ v, label }))
      .sort((a,b) => a.label.localeCompare(b.label));
  }, [tarefas]);
  const opcoesProjetos = useMemo(() => {
    const m = new Map();
    tarefas.forEach(t => {
      if (t.negocio_id && !m.has(t.negocio_id)) {
        m.set(t.negocio_id, t.negocio_descricao || t.negocio_id);
      }
    });
    return Array.from(m.entries()).map(([v, label]) => ({ v, label }))
      .sort((a,b) => a.label.localeCompare(b.label));
  }, [tarefas]);
  const opcoesSolicitantes = useMemo(() => {
    const m = new Map();
    tarefas.forEach(t => {
      if (t.solicitante_id && !m.has(t.solicitante_id)) {
        m.set(t.solicitante_id, t.solicitante_nome || t.solicitante_id);
      }
    });
    return Array.from(m.entries()).map(([v, label]) => ({ v, label }))
      .sort((a,b) => a.label.localeCompare(b.label));
  }, [tarefas]);
  const opcoesResponsaveis = useMemo(() => {
    const m = new Map();
    tarefas.forEach(t => {
      if (t.responsavel_id && !m.has(t.responsavel_id)) {
        m.set(t.responsavel_id, t.responsavel_nome || t.responsavel_id);
      }
    });
    return Array.from(m.entries()).map(([v, label]) => ({ v, label }))
      .sort((a,b) => a.label.localeCompare(b.label));
  }, [tarefas]);

  const limparFiltros = () => setFiltros(f => ({ ...FILTROS_DEFAULT, papel: f.papel, ordem: f.ordem, busca: f.busca }));

  return (
    <div style={{
      backgroundColor: COR.branco,
      border: `1px solid ${COR.borda}`,
      borderRadius: '8px',
      marginBottom: '12px',
      overflow: 'visible',
    }}>
      {/* LINHA 1: papel + busca + ordem + botão filtros */}
      <div style={{
        padding: '10px 14px',
        display: 'flex', alignItems: 'center', gap: '8px',
        flexWrap: 'wrap',
      }}>
        <span style={{
          fontSize: '13px', fontWeight: '500', color: COR.muted,
          fontFamily: "'Manrope', sans-serif",
        }}>
          {totalRegistros != null
            ? `${totalRegistros} registro${totalRegistros !== 1 ? 's' : ''}`
            : ''}
        </span>

        <div style={{ display: 'flex', gap: '4px', marginLeft: '12px' }}>
          {[
            { v: 'atribuidas',          label: 'Atribuídas a mim' },
            { v: 'criadas',             label: 'Criadas por mim' },
            { v: 'criadas_por_outros',  label: 'Criadas por outros' },
            { v: 'criadas_para_outros', label: 'Criadas para outros' },
            { v: 'citadas',             label: 'Citado' },
            { v: 'todas',               label: 'Todos' },
          ].map(o => {
            const ativo = filtros.papel === o.v;
            return (
              <button
                key={o.v}
                onClick={() => atualizarFiltro('papel', o.v)}
                style={{
                  padding: '5px 10px', fontSize: '11px', fontWeight: '600',
                  border: `1px solid ${ativo ? COR.azul : COR.borda}`,
                  borderRadius: '4px',
                  backgroundColor: ativo ? COR.azul : COR.branco,
                  color: ativo ? COR.branco : COR.texto,
                  cursor: 'pointer',
                  fontFamily: "'Manrope', sans-serif",
                }}
              >{o.label}</button>
            );
          })}
        </div>

        <div style={{ flex: 1 }} />

        {/* r220a: lupa em SVG por cima do input — placeholder não aceita markup */}
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <span style={{
            position: 'absolute', left: '8px', display: 'inline-flex',
            pointerEvents: 'none', color: COR.muted,
          }}>
            <IconeBusca tamanho={13} cor={COR.muted} />
          </span>
          <input type="text" placeholder="Buscar..." value={filtros.busca}
                 onChange={e => atualizarFiltro('busca', e.target.value)}
                 style={{
                   width: '200px', height: '28px', padding: '0 10px 0 26px',
                   border: `1px solid ${COR.borda}`, borderRadius: '4px',
                   fontSize: '12px', fontFamily: "'Manrope', sans-serif",
                   boxSizing: 'border-box',
                 }} />
        </div>

        {/* r220: dropdown próprio com ícone SVG por opção (o <option> nativo
            só aceita texto, daí os emojis de antes). */}
        <SelectOrdemTarefas
          valor={filtros.ordem}
          onChange={v => atualizarFiltro('ordem', v)}
        />

        <button
          onClick={() => setFiltrosExpandidos(e => !e)}
          style={{
            height: '28px', padding: '0 10px',
            border: `1px solid ${totalFiltrosAtivos > 0 ? COR.azul : COR.borda}`,
            borderRadius: '4px',
            backgroundColor: filtrosExpandidos
              ? COR.azulBg
              : (totalFiltrosAtivos > 0 ? COR.azulBg : COR.branco),
            color: totalFiltrosAtivos > 0 ? COR.azul : COR.texto,
            fontSize: '11px', fontWeight: '600',
            cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
          title="Mostrar/ocultar filtros avançados"
        >
          <span>⚙ Filtros</span>
          {totalFiltrosAtivos > 0 && (
            <span style={{
              backgroundColor: COR.azul, color: COR.branco,
              fontSize: '9px', fontWeight: '700',
              padding: '1px 5px', borderRadius: '8px',
            }}>{totalFiltrosAtivos}</span>
          )}
          <span style={{ fontSize: '8px' }}>{filtrosExpandidos ? '▴' : '▾'}</span>
        </button>

        {totalFiltrosAtivos > 0 && (
          <button onClick={limparFiltros}
                  title="Limpar filtros (mantém papel, ordem e busca)"
                  style={{
                    height: '28px', padding: '0 8px',
                    border: `1px solid ${COR.borda}`, borderRadius: '4px',
                    backgroundColor: COR.branco, color: COR.muted,
                    fontSize: '11px', fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Manrope', sans-serif",
                  }}>× Limpar</button>
        )}

        {showActions && (
          <SePode rotina="tarefas" acao="incluir">
            <BtnIcone icone="incluir" onClick={onNovaTarefa} title="Nova tarefa" cor={COR.azul} solido />
          </SePode>
        )}
      </div>

      {/* LINHA 2: Filtros avançados */}
      {filtrosExpandidos && (
        <div style={{
          padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: '8px',
          flexWrap: 'wrap',
          backgroundColor: COR.cinzaBg,
          borderTop: `1px solid ${COR.borda}`,
        }}>
          <MultiSelect compacto label="Status"
            valores={filtros.status_execucao}
            onChange={v => atualizarFiltro('status_execucao', v)}
            opcoes={Object.entries(STATUS_EXECUCAO).map(([v, info]) => ({ v, label: info.label, cor: info.cor }))}
            largura="140px" />
          <MultiSelect compacto label="Setor"
            valores={filtros.setor}
            onChange={v => atualizarFiltro('setor', v)}
            opcoes={Object.entries(SETOR).map(([v, info]) => ({ v, label: info.label, cor: info.cor }))}
            largura="130px" />
          <MultiSelect compacto label="Prioridade"
            valores={filtros.prioridade}
            onChange={v => atualizarFiltro('prioridade', v)}
            opcoes={Object.entries(PRIORIDADE).map(([v, info]) => ({ v, label: info.label, cor: info.cor }))}
            largura="130px" />
          <MultiSelect compacto label="Cliente"
            valores={filtros.cliente_ids}
            onChange={v => atualizarFiltro('cliente_ids', v)}
            opcoes={opcoesClientes} largura="160px" />
          <MultiSelect compacto label="Projeto"
            valores={filtros.negocio_ids}
            onChange={v => atualizarFiltro('negocio_ids', v)}
            opcoes={opcoesProjetos} largura="160px" />
          <MultiSelect compacto label="Solicitante"
            valores={filtros.solicitante_ids}
            onChange={v => atualizarFiltro('solicitante_ids', v)}
            opcoes={opcoesSolicitantes} largura="140px" />
          <MultiSelect compacto label="Responsável"
            valores={filtros.responsavel_ids}
            onChange={v => atualizarFiltro('responsavel_ids', v)}
            opcoes={opcoesResponsaveis} largura="140px" />

          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '0 6px', borderLeft: `1px solid ${COR.borda}`,
          }}>
            <span style={{ fontSize: '10px', color: COR.muted, fontWeight: '600' }}>Programada:</span>
            <input type="date" value={filtros.data_prog_de}
                   onChange={e => atualizarFiltro('data_prog_de', e.target.value)}
                   style={dataStyle} />
            <span style={{ fontSize: '10px', color: COR.muted }}>até</span>
            <input type="date" value={filtros.data_prog_ate}
                   onChange={e => atualizarFiltro('data_prog_ate', e.target.value)}
                   style={dataStyle} />
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '0 6px', borderLeft: `1px solid ${COR.borda}`,
          }}>
            <span style={{ fontSize: '10px', color: COR.muted, fontWeight: '600' }}>Deadline:</span>
            <input type="date" value={filtros.deadline_de}
                   onChange={e => atualizarFiltro('deadline_de', e.target.value)}
                   style={dataStyle} />
            <span style={{ fontSize: '10px', color: COR.muted }}>até</span>
            <input type="date" value={filtros.deadline_ate}
                   onChange={e => atualizarFiltro('deadline_ate', e.target.value)}
                   style={dataStyle} />
          </div>
        </div>
      )}
    </div>
  );
}

const dataStyle = {
  height: '28px', padding: '0 6px',
  border: `1px solid ${COR.borda}`, borderRadius: '4px',
  fontSize: '11px', fontFamily: "'Manrope', sans-serif",
};

export default TarefasFiltrosBar;
